import type {
	SerializedAllFeaturesEditorState,
	SerializedAnyBlockNode,
} from '@/admin/lexical/features'
import { ContentLayoutProvider } from '@/contexts/ContentLayoutContext'
import type { ContentLayout } from '@/helpers/layout'
import { CMS_BLOCKS_CLASS, getLayoutClasses } from '@/helpers/layout'
import { internalLinkToHref } from '@/helpers/transforms'
import type { SerializedLinkNode } from '@payloadcms/richtext-lexical'
import {
	type JSXConvertersFunction,
	LinkJSXConverter,
	RichText,
} from '@payloadcms/richtext-lexical/react'
import { cx } from 'cva'
import type { FC } from 'react'
import { AccordionConverter } from './converters/AccordionConverter'
import { AllVenuesConverter } from './converters/AllVenuesConverter'
import { ButtonFormOverlayConverter } from './converters/ButtonFormOverlayConverter'
import { ButtonLinkConverter } from './converters/ButtonLinkConverter'
import { ContrastSectionConverter } from './converters/ContrastSectionConverter'
import { CTACardConverter } from './converters/CTACardConverter'
import { FeatureIconsGridConverter } from './converters/FeatureIconsGridConverter'
import { FeatureImagesGridConverter } from './converters/FeatureImagesGridConverter'
import { FullWidthImageConverter } from './converters/FullWidthImageConverter'
import { HeroImageConverter } from './converters/HeroImageConverter'
import { HeroTitleConverter } from './converters/HeroTitleConverter'
import { HeroVideoConverter } from './converters/HeroVideoConverter'
import { ImageCarouselConverter } from './converters/ImagesCarouselConverter'
import { ImageWithCaptionConverter } from './converters/ImageWithCaptionConverter'
import { InlineMenuConverter } from './converters/InlineMenuConverter'
import { InPageNavigationConverter } from './converters/InPageNavigationConverter'
import { InstagramEmbedConverter } from './converters/InstagramEmbedConverter'
import { LatestNewsConverter } from './converters/LatestNewsConverter'
import { LatestVenueNewsConverter } from './converters/LatestVenueNewsConverter'
import { LegalTextConverter } from './converters/LegalTextConverter'
import { LinksGridConverter } from './converters/LinksGridConverter'
import { NewsArticleCardConverter } from './converters/NewsArticleCardConverter'
import { NewsArticlesCarouselConverter } from './converters/NewsArticlesCarouselConverter'
import { NewsHighlightConverter } from './converters/NewsHighlightConverter'
import { RoadMapConverter } from './converters/RoadMapConverter'
import { StaffConverter } from './converters/StaffConverter'
import { StatBlocksMasonryConverter } from './converters/StatBlocksMasonryConverter'
import { SubscribeFormConverter } from './converters/SubscribeFormConverter'
import { SubtitleConverter } from './converters/SubtitleConverter'
import {
	DisplayParagraphConverter,
	HeadingConverter,
	HorizontalRuleConverter,
	LargeParagraphConverter,
	ListConverter,
	ParagraphConverter,
	SmallParagraphConverter,
} from './converters/TextConverters'
import { TextCTAConverter } from './converters/TextCTAConverter'
import { TextWithGalleryAsideConverter } from './converters/TextWithGalleryAsideConverter'
import { TextWithIconAsideConverter } from './converters/TextWithIconAsideConverter'
import { TextWithImageAsideConverter } from './converters/TextWithImageAsideConverter'
import { TimelineConverter } from './converters/TimelineConverter'
import { TopicCardsConverter } from './converters/TopicCardsConverter'
import { TwoColumnTextConverter } from './converters/TwoColumnTextConverter'
import { VenueCardConverter } from './converters/VenueCardConverter'
import { VideoConverter } from './converters/VideoConverter'

export type RenderContentBlocksProps = {
	layout?: ContentLayout
	data: SerializedAllFeaturesEditorState | null | undefined
}

const internalDocToHref = ({ linkNode }: { linkNode: SerializedLinkNode }) => {
	return internalLinkToHref(linkNode.fields)
}

const converters: JSXConvertersFunction<SerializedAnyBlockNode> = ({ defaultConverters }) => {
	return {
		...defaultConverters,
		// Link URL resolver
		...LinkJSXConverter({ internalDocToHref }),
		// Custom paragraph types
		'display-paragraph': DisplayParagraphConverter,
		paragraph: ParagraphConverter,
		'large-paragraph': LargeParagraphConverter,
		'small-paragraph': SmallParagraphConverter,
		heading: HeadingConverter,
		list: ListConverter,
		horizontalrule: HorizontalRuleConverter,
		// @see https://payloadcms.com/docs/rich-text/converting-jsx#richtext-to-jsx
		blocks: {
			'hero-video': HeroVideoConverter,
			'hero-image': HeroImageConverter,
			'hero-title': HeroTitleConverter,
			'two-column-text': TwoColumnTextConverter,
			'button-link': ButtonLinkConverter,
			'button-form-overlay': ButtonFormOverlayConverter,
			'cta-card': CTACardConverter,
			subtitle: SubtitleConverter,
			'legal-text': LegalTextConverter,
			'image-with-caption': ImageWithCaptionConverter,
			'stat-blocks-masonry': StatBlocksMasonryConverter,
			'news-highlight': NewsHighlightConverter,
			'all-venues': AllVenuesConverter,
			'venue-card': VenueCardConverter,
			'images-carousel': ImageCarouselConverter,
			'topic-cards': TopicCardsConverter,
			timeline: TimelineConverter,
			'feature-icons-grid': FeatureIconsGridConverter,
			'feature-images-grid': FeatureImagesGridConverter,
			accordion: AccordionConverter,
			'links-grid': LinksGridConverter,
			'contrast-section': ContrastSectionConverter,
			'text-with-image-aside': TextWithImageAsideConverter,
			'subscribe-form': SubscribeFormConverter,
			'text-with-gallery-aside': TextWithGalleryAsideConverter,
			'text-with-icon-aside': TextWithIconAsideConverter,
			'text-cta': TextCTAConverter,
			roadmap: RoadMapConverter,
			'latest-news': LatestNewsConverter,
			'latest-venue-news': LatestVenueNewsConverter,
			'news-article-card': NewsArticleCardConverter,
			'full-width-image': FullWidthImageConverter,
			'inline-menu': InlineMenuConverter,
			'in-page-navigation': InPageNavigationConverter,
			staff: StaffConverter,
			'news-articles-carousel': NewsArticlesCarouselConverter,
			video: VideoConverter,
			'instagram-embed': InstagramEmbedConverter,
		},
	}
}

export const RenderLexicalContent: FC<RenderContentBlocksProps> = ({ layout = 'wide', data }) => {
	return (
		<ContentLayoutProvider layout={layout}>
			{data ? (
				<RichText
					converters={converters}
					data={data}
					className={cx(CMS_BLOCKS_CLASS, getLayoutClasses(layout), 'grow')}
				/>
			) : null}
		</ContentLayoutProvider>
	)
}
